/**
 * JournalPage — Dagboek (spec §8.7).
 *
 * One entry per local date, loaded via repositories.journal.getByDate and
 * upserted via repositories.journal.save (server-confirmed writes only).
 *
 * P0 truthfulness & privacy rules implemented here:
 * - Sync copy is always honest: 'In cloud opgeslagen' appears ONLY after the
 *   repository confirms the write; drafts are labelled 'Op apparaat opgeslagen'
 *   / 'Alleen op dit apparaat'.
 * - Unsaved work autosaves to a device-only localStorage draft per date
 *   ('rust-journal-drafts'), is restored on return, and is cleared only after
 *   a confirmed cloud save (or an explicit discard/delete).
 * - A leave guard (react-router useBlocker + confirm dialog) protects dirty
 *   forms on route leave and date switches.
 * - SUPABASE_NOT_CONFIGURED: the form keeps working as an explicit device-only
 *   draft with an honest Banner — no fake sync, no silent fallback.
 * - Privacy: journal text is NEVER logged, never sent to analytics, and error
 *   feedback uses generic Dutch copy. Text leaves the device only via the
 *   confirmed cloud write.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useBlocker } from 'react-router-dom'
import {
  Banner,
  Button,
  Card,
  ConfirmDestructiveAction,
  EmptyState,
  ErrorState,
  IconButton,
  SaveBar,
  Skeleton,
  StatusChip,
  SyncStatus,
  useToast,
} from '@/components/ui'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DropletIcon,
  HeartIcon,
  JournalIcon,
  MoonIcon,
  TrashIcon,
} from '@/components/icons'
import { PageHeader } from '@/components/navigation'
import { repositories } from '@/services/repositories'
import { isCloudConfigured } from '@/lib/supabase/client'
import { toAppError } from '@/lib/supabase/errors'
import type { AppError, JournalEntry, SyncSaveState } from '@/types'
import {
  addDaysISO,
  formatDateLongNL,
  isFutureISO,
  isTodayISO,
  todayISODate,
} from './journalDates'
import {
  getJournalDraft,
  loadJournalDrafts,
  removeJournalDraft,
  saveJournalDraft,
  type JournalDraftMap,
} from './journalDrafts'
import {
  EMPTY_JOURNAL_VALUES,
  ENERGY_OPTIONS,
  MOOD_OPTIONS,
  STRESS_OPTIONS,
  scaleLabel,
  valuesEqual,
  valuesFromEntry,
  valuesToNewEntry,
  type JournalFormValues,
} from './scaleOptions'
import { JournalEntryForm } from './JournalEntryForm'
import './journal.css'

type LoadState = 'loading' | 'ready' | 'error'
type ListState = 'loading' | 'ready' | 'error' | 'unavailable'

const PREVIOUS_ENTRIES_LIMIT = 30

function snippet(text: string, max = 90): string {
  const collapsed = text.replace(/\s+/g, ' ').trim()
  return collapsed.length > max ? `${collapsed.slice(0, max - 1)}…` : collapsed
}

function draftToValues(draft: JournalFormValues): JournalFormValues {
  return {
    mood: draft.mood,
    energy: draft.energy,
    stress: draft.stress,
    wentWell: draft.wentWell,
    wasDifficult: draft.wasDifficult,
    rememberTomorrow: draft.rememberTomorrow,
    freewriting: draft.freewriting,
    tags: [...draft.tags],
  }
}

export default function JournalPage() {
  const { showToast } = useToast()

  const [selectedDate, setSelectedDate] = useState<string>(todayISODate)
  const [loadRetry, setLoadRetry] = useState(0)
  const [entry, setEntry] = useState<JournalEntry | null>(null)
  const [values, setValues] = useState<JournalFormValues>(EMPTY_JOURNAL_VALUES)
  const [baseline, setBaseline] = useState<JournalFormValues>(EMPTY_JOURNAL_VALUES)
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [loadError, setLoadError] = useState<AppError | null>(null)
  const [syncState, setSyncState] = useState<SyncSaveState | null>(null)
  const [saving, setSaving] = useState(false)
  const [cloudAvailable, setCloudAvailable] = useState<boolean>(isCloudConfigured)

  const [entries, setEntries] = useState<JournalEntry[]>([])
  const [listState, setListState] = useState<ListState>(isCloudConfigured() ? 'loading' : 'unavailable')
  const [localDrafts, setLocalDrafts] = useState<JournalDraftMap>(loadJournalDrafts)

  const [announcement, setAnnouncement] = useState('')
  const [pendingDateSwitch, setPendingDateSwitch] = useState<string | null>(null)
  const [discardOpen, setDiscardOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const dirty = loadState === 'ready' && !valuesEqual(values, baseline)
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty
  const valuesRef = useRef(values)
  valuesRef.current = values
  const entryRef = useRef(entry)
  entryRef.current = entry
  const selectedDateRef = useRef(selectedDate)
  selectedDateRef.current = selectedDate
  const loadSeq = useRef(0)

  const announce = (message: string) => setAnnouncement(message)

  /**
   * Any edit invalidates the last confirmed sync state: status is cleared
   * until the device-only draft autosave ('Op apparaat opgeslagen') or a
   * confirmed cloud save makes it truthful again. A failed state sticks so
   * the retry context stays visible.
   */
  const handleValuesChange = (next: JournalFormValues) => {
    setValues(next)
    setSyncState((prev) => (prev === 'syncing' || prev === 'sync_failed' ? prev : null))
  }

  /** Synchronously persists the current form as a device-only draft. */
  const flushDraft = useCallback(
    (state: SyncSaveState = 'local_saved') => {
      if (!dirtyRef.current) return
      saveJournalDraft(selectedDateRef.current, {
        ...valuesRef.current,
        state,
        basedOnEntryUpdatedAt: entryRef.current?.updatedAt ?? null,
      })
      setLocalDrafts(loadJournalDrafts())
    },
    [],
  )

  // -------------------------------------------------------------------------
  // Loading one entry per selected date
  // -------------------------------------------------------------------------
  useEffect(() => {
    const date = selectedDate
    const seq = ++loadSeq.current
    setLoadState('loading')
    setLoadError(null)

    const applyLocal = (draftState: SyncSaveState | null) => {
      const draft = getJournalDraft(date)
      setEntry(null)
      setBaseline(EMPTY_JOURNAL_VALUES)
      if (draft) {
        setValues(draftToValues(draft))
        setSyncState(draft.state)
        announce('Concept van dit apparaat hersteld.')
      } else {
        setValues(EMPTY_JOURNAL_VALUES)
        setSyncState(draftState)
      }
      setLoadState('ready')
      setLocalDrafts(loadJournalDrafts())
    }

    if (!isCloudConfigured()) {
      setCloudAvailable(false)
      applyLocal(null)
      return
    }

    let cancelled = false
    void (async () => {
      try {
        const loaded = await repositories.journal.getByDate(date)
        if (cancelled || loadSeq.current !== seq) return
        const entryValues = loaded ? valuesFromEntry(loaded) : EMPTY_JOURNAL_VALUES
        const draft = getJournalDraft(date)
        setEntry(loaded)
        setBaseline(entryValues)
        if (draft && !valuesEqual(draftToValues(draft), entryValues)) {
          // Unsaved local work takes precedence and stays clearly labelled.
          setValues(draftToValues(draft))
          setSyncState(draft.state)
          announce('Concept van dit apparaat hersteld.')
        } else {
          if (draft) {
            removeJournalDraft(date)
            setLocalDrafts(loadJournalDrafts())
          }
          setValues(entryValues)
          setSyncState(loaded ? 'cloud_saved' : null)
        }
        setLoadState('ready')
      } catch (error) {
        if (cancelled || loadSeq.current !== seq) return
        const appError = toAppError(error, 'database')
        if (appError.code === 'SUPABASE_NOT_CONFIGURED') {
          setCloudAvailable(false)
          applyLocal(null)
          return
        }
        setLoadError(appError)
        setLoadState('error')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [selectedDate, loadRetry])

  // -------------------------------------------------------------------------
  // Previous entries list
  // -------------------------------------------------------------------------
  const refreshList = useCallback(async () => {
    if (!isCloudConfigured()) {
      setListState('unavailable')
      return
    }
    setListState('loading')
    try {
      const list = await repositories.journal.list(PREVIOUS_ENTRIES_LIMIT)
      setEntries(list)
      setListState('ready')
    } catch (error) {
      const appError = toAppError(error, 'database')
      setListState(appError.code === 'SUPABASE_NOT_CONFIGURED' ? 'unavailable' : 'error')
      if (appError.code === 'SUPABASE_NOT_CONFIGURED') setCloudAvailable(false)
    }
  }, [])

  useEffect(() => {
    void refreshList()
  }, [refreshList])

  // -------------------------------------------------------------------------
  // Device-only draft autosave (debounced)
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!dirty || loadState !== 'ready') return
    const handle = setTimeout(() => {
      flushDraft(syncState === 'sync_failed' ? 'sync_failed' : 'local_saved')
      setSyncState((prev) => (prev === 'sync_failed' ? prev : 'local_saved'))
    }, 500)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, dirty, loadState, selectedDate])

  // Browser tab close / reload guard while dirty.
  useEffect(() => {
    if (!dirty) return
    const handler = (event: BeforeUnloadEvent) => {
      flushDraft()
      event.preventDefault()
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty, flushDraft])

  // Route-leave guard (react-router v7 data router blocker).
  const blocker = useBlocker(dirty)

  // -------------------------------------------------------------------------
  // Save — cloud truth only after the server confirms
  // -------------------------------------------------------------------------
  const handleSave = async () => {
    if (saving || loadState !== 'ready') return
    setSaving(true)
    setSyncState('syncing')
    announce('Bezig met opslaan…')

    if (!cloudAvailable) {
      // Explicit device-only mode: the local draft IS the saved truth here,
      // so the form is no longer dirty after this save. Never fake cloud copy.
      flushDraft('local_saved')
      setBaseline(values)
      setSyncState('local_saved')
      setSaving(false)
      announce('Op apparaat opgeslagen. Cloudverbinding is niet ingesteld.')
      showToast({
        message: 'Op apparaat opgeslagen. Cloudverbinding is niet ingesteld.',
        tone: 'info',
      })
      return
    }

    try {
      const saved = await repositories.journal.save(valuesToNewEntry(selectedDate, values))
      const savedValues = valuesFromEntry(saved)
      setEntry(saved)
      setBaseline(savedValues)
      setValues(savedValues)
      removeJournalDraft(selectedDate)
      setLocalDrafts(loadJournalDrafts())
      setSyncState('cloud_saved')
      announce('In cloud opgeslagen.')
      showToast({ message: 'Dagboek opgeslagen in de cloud.', tone: 'success' })
      void refreshList()
    } catch (error) {
      const appError = toAppError(error, 'database')
      if (appError.code === 'SUPABASE_NOT_CONFIGURED') {
        setCloudAvailable(false)
        flushDraft('local_saved')
        setBaseline(values)
        setSyncState('local_saved')
        announce('Op apparaat opgeslagen. Cloudverbinding is niet ingesteld.')
        showToast({
          message: 'Cloudverbinding is niet ingesteld. Je entry blijft op dit apparaat bewaard.',
          tone: 'info',
        })
      } else {
        const queued = appError.code === 'NETWORK_UNREACHABLE'
        const failedState: SyncSaveState = queued ? 'sync_queued' : 'sync_failed'
        flushDraft(failedState)
        setSyncState(failedState)
        announce('Opslaan in de cloud is mislukt. Je concept blijft op dit apparaat bewaard.')
        showToast({
          message: 'Opslaan in de cloud is mislukt. Je concept blijft op dit apparaat bewaard.',
          tone: 'error',
        })
      }
    } finally {
      setSaving(false)
    }
  }

  // -------------------------------------------------------------------------
  // Date navigation with dirty guard
  // -------------------------------------------------------------------------
  const switchDate = (date: string) => {
    flushDraft()
    setPendingDateSwitch(null)
    setSelectedDate(date)
  }

  const requestDateChange = (date: string) => {
    if (date === selectedDate) return
    if (dirtyRef.current) {
      setPendingDateSwitch(date)
    } else {
      switchDate(date)
    }
  }

  const goByDays = (days: number) => {
    const next = addDaysISO(selectedDate, days)
    if (next) requestDateChange(next)
  }

  // -------------------------------------------------------------------------
  // Discard changes (SaveBar cancel) — explicit, removes the device draft
  // -------------------------------------------------------------------------
  const handleDiscard = () => {
    setValues(baseline)
    removeJournalDraft(selectedDate)
    setLocalDrafts(loadJournalDrafts())
    setSyncState(entry ? 'cloud_saved' : null)
    setDiscardOpen(false)
    announce('Wijzigingen verworpen.')
  }

  // -------------------------------------------------------------------------
  // Delete entry (ConfirmDestructiveAction)
  // -------------------------------------------------------------------------
  const handleDelete = async () => {
    if (!entry || deleting) return
    setDeleting(true)
    try {
      await repositories.journal.remove(entry.id)
      setEntry(null)
      setValues(EMPTY_JOURNAL_VALUES)
      setBaseline(EMPTY_JOURNAL_VALUES)
      removeJournalDraft(selectedDate)
      setLocalDrafts(loadJournalDrafts())
      setSyncState(null)
      setDeleteOpen(false)
      announce('Dagboekentry verwijderd.')
      showToast({ message: 'Dagboekentry verwijderd.', tone: 'success' })
      void refreshList()
    } catch {
      // Generic copy only — journal content never appears in errors.
      showToast({ message: 'Verwijderen is mislukt. Probeer het opnieuw.', tone: 'error' })
    } finally {
      setDeleting(false)
    }
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  const atToday = isTodayISO(selectedDate)
  const nextDisabled = isFutureISO(addDaysISO(selectedDate, 1) ?? selectedDate)
  const draftDates = Object.keys(localDrafts)
    .filter((date) => date !== selectedDate)
    .sort()
    .reverse()

  return (
    <div className="journal-page">
      <PageHeader title="Dagboek" subtitle="Eén entry per dag. Alles blijft privé." />

      <p className="journal-sr-only" role="status" aria-live="polite">
        {announcement}
      </p>

      {!cloudAvailable ? (
        <Banner tone="info">
          Cloudverbinding is niet ingesteld. Je dagboek wordt alleen op dit apparaat bewaard
          (Alleen op dit apparaat) en niet gesynchroniseerd.
        </Banner>
      ) : null}

      {/* Date navigation */}
      <Card padding="sm" className="journal-datenav">
        <div className="journal-datenav-row">
          <IconButton aria-label="Vorige dag" onClick={() => goByDays(-1)}>
            <ChevronLeftIcon />
          </IconButton>
          <div className="journal-datenav-current">
            <span className="journal-datenav-date">{formatDateLongNL(selectedDate)}</span>
            {atToday ? <StatusChip status="info">Vandaag</StatusChip> : null}
          </div>
          <IconButton
            aria-label="Volgende dag"
            onClick={() => goByDays(1)}
            disabled={nextDisabled}
          >
            <ChevronRightIcon />
          </IconButton>
        </div>
        {!atToday ? (
          <Button variant="ghost" size="sm" fullWidth onClick={() => requestDateChange(todayISODate())}>
            Ga naar vandaag
          </Button>
        ) : null}
      </Card>

      {/* Entry form */}
      {loadState === 'loading' ? (
        <div className="journal-loading" aria-label="Dagboekentry wordt geladen">
          <Skeleton lines={5} />
        </div>
      ) : null}

      {loadState === 'error' && loadError ? (
        <ErrorState
          title="Dagboekentry laden mislukt"
          error={loadError}
          onRetry={loadError.retryable ? () => setLoadRetry((n) => n + 1) : undefined}
        />
      ) : null}

      {loadState === 'ready' ? (
        <Card padding="lg" className="journal-entry">
          <div className="journal-entry-status">
            <SyncStatus state={syncState} />
            {!cloudAvailable ? <StatusChip status="neutral">Alleen op dit apparaat</StatusChip> : null}
          </div>

          <JournalEntryForm values={values} onChange={handleValuesChange} disabled={saving} />

          {entry && cloudAvailable ? (
            <div className="journal-entry-danger">
              <Button variant="ghost" onClick={() => setDeleteOpen(true)}>
                <TrashIcon size={18} />
                Entry verwijderen
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}

      <SaveBar
        visible={dirty}
        saving={saving}
        saveLabel="Opslaan"
        onSave={() => void handleSave()}
        onCancel={() => setDiscardOpen(true)}
        state={syncState}
      />

      {/* Device-only drafts for other dates */}
      {draftDates.length > 0 ? (
        <section className="journal-drafts" aria-label="Concepten op dit apparaat">
          <h2 className="journal-section-title">Concepten op dit apparaat</h2>
          {draftDates.map((date) => (
            <button
              key={date}
              type="button"
              className="journal-entry-card"
              onClick={() => requestDateChange(date)}
            >
              <span className="journal-entry-card-heading">
                <span className="journal-entry-card-date">{formatDateLongNL(date)}</span>
                <StatusChip status="neutral">Alleen op dit apparaat</StatusChip>
              </span>
              <SyncStatus state={localDrafts[date].state} />
            </button>
          ))}
        </section>
      ) : null}

      {/* Previous entries */}
      <section className="journal-history" aria-label="Eerdere entries">
        <h2 className="journal-section-title">Eerdere entries</h2>

        {listState === 'loading' ? <Skeleton lines={3} /> : null}

        {listState === 'unavailable' ? (
          <Banner tone="info">
            Eerdere entries laden niet zonder cloudverbinding. Concepten op dit apparaat blijven
            bewaard.
          </Banner>
        ) : null}

        {listState === 'error' ? (
          <Banner
            tone="error"
            action={
              <Button variant="secondary" size="sm" onClick={() => void refreshList()}>
                Opnieuw proberen
              </Button>
            }
          >
            Eerdere entries laden is mislukt.
          </Banner>
        ) : null}

        {listState === 'ready' && entries.length === 0 ? (
          <EmptyState
            icon={<JournalIcon size={28} />}
            title="Nog geen eerdere entries"
            body="Schrijf vandaag je eerste dagboekentry."
          />
        ) : null}

        {listState === 'ready' && entries.length > 0 ? (
          <div className="journal-history-list">
            {entries.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`journal-entry-card${
                  item.date === selectedDate ? ' journal-entry-card--current' : ''
                }`}
                aria-current={item.date === selectedDate ? 'date' : undefined}
                onClick={() => requestDateChange(item.date)}
              >
                <span className="journal-entry-card-heading">
                  <span className="journal-entry-card-date">{formatDateLongNL(item.date)}</span>
                  {isTodayISO(item.date) ? <StatusChip status="info">Vandaag</StatusChip> : null}
                </span>
                <span className="journal-entry-card-scales">
                  <span className="journal-entry-card-scale">
                    <HeartIcon size={16} />
                    <span>
                      Stemming {item.mood}/5 · {scaleLabel(MOOD_OPTIONS, item.mood)}
                    </span>
                  </span>
                  <span className="journal-entry-card-scale">
                    <DropletIcon size={16} />
                    <span>
                      Energie {item.energy}/5 · {scaleLabel(ENERGY_OPTIONS, item.energy)}
                    </span>
                  </span>
                  <span className="journal-entry-card-scale">
                    <MoonIcon size={16} />
                    <span>
                      Stress {item.stress}/5 · {scaleLabel(STRESS_OPTIONS, item.stress)}
                    </span>
                  </span>
                </span>
                {item.wentWell ? (
                  <span className="journal-entry-card-snippet">{snippet(item.wentWell)}</span>
                ) : null}
                {item.tags.length > 0 ? (
                  <span className="journal-entry-card-tags">
                    {item.tags.map((tag) => (
                      <span key={tag} className="journal-entry-card-tag">
                        {tag}
                      </span>
                    ))}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
      </section>

      {/* Route-leave guard */}
      <ConfirmDestructiveAction
        open={blocker.state === 'blocked'}
        title="Wijzigingen nog niet opgeslagen in de cloud"
        body="Je wijzigingen blijven als concept op dit apparaat bewaard. Wil je deze pagina toch verlaten?"
        confirmLabel="Pagina verlaten"
        onConfirm={() => {
          flushDraft()
          blocker.proceed?.()
        }}
        onCancel={() => blocker.reset?.()}
      />

      {/* Date-switch guard */}
      <ConfirmDestructiveAction
        open={pendingDateSwitch !== null}
        title="Wijzigingen nog niet opgeslagen in de cloud"
        body="Je wijzigingen blijven als concept op dit apparaat bewaard. Wil je toch naar een andere dag?"
        confirmLabel="Andere dag openen"
        onConfirm={() => {
          if (pendingDateSwitch) switchDate(pendingDateSwitch)
        }}
        onCancel={() => setPendingDateSwitch(null)}
      />

      {/* Discard changes */}
      <ConfirmDestructiveAction
        open={discardOpen}
        title="Wijzigingen verwerpen"
        body="Je niet-opgeslagen wijzigingen en het concept op dit apparaat worden verwijderd."
        confirmLabel="Verwerpen"
        onConfirm={handleDiscard}
        onCancel={() => setDiscardOpen(false)}
      />

      {/* Delete entry */}
      <ConfirmDestructiveAction
        open={deleteOpen}
        title="Dagboekentry verwijderen"
        body={`Weet je zeker dat je de entry van ${formatDateLongNL(selectedDate)} wilt verwijderen? Dit kan niet ongedaan worden.`}
        confirmLabel={deleting ? 'Verwijderen…' : 'Verwijderen'}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteOpen(false)}
      />
    </div>
  )
}
